"use client";

import { usePathname } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

export type AudioTrackingProps = Record<string, unknown>;

export type NativeAudioPlayOptions = {
  id: string;
  url: string;
  loop?: boolean;
  startTime?: number;
  trackingProps?: AudioTrackingProps;

  // Session-scoped callbacks
  onTimeUpdate?: (currentTime: number, duration: number) => void;
  onDurationChange?: (duration: number) => void;
  onPlayingChange?: (isPlaying: boolean) => void;
  onEnded?: () => void;
  onError?: (error: unknown) => void;
};

type NativeAudioContextType = {
  play: (opts: NativeAudioPlayOptions) => Promise<void>;
  togglePlay: (opts: NativeAudioPlayOptions) => Promise<void>;
  pause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  playingId: string | null;
  isPaused: boolean;
  audioRef: React.RefObject<HTMLAudioElement | null>;
};

const NativeAudioContext = createContext<NativeAudioContextType | null>(null);

/**
 * Native audio provider that uses a single HTMLAudioElement to enforce one-at-a-time playback.
 * Provides session-scoped callbacks for time updates and other media events.
 */
export function NativeAudioProvider({ children }: { children: React.ReactNode }) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const pathname = usePathname();

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playSessionRef = useRef(0);
  const isMountedRef = useRef(true);
  const isStoppingRef = useRef(false);

  // Callback refs
  const onTimeUpdateRef = useRef<((currentTime: number, duration: number) => void) | null>(null);
  const onDurationChangeRef = useRef<((duration: number) => void) | null>(null);
  const isPlayingRef = useRef<((isPlaying: boolean) => void) | null>(null);
  const onEndedRef = useRef<(() => void) | null>(null);
  const onErrorRef = useRef<((error: unknown) => void) | null>(null);

  // Tracking props ref
  const currentTrackingPropsRef = useRef<AudioTrackingProps | null>(null);
  const currentIdRef = useRef<string | null>(null);
  const currentUrlRef = useRef<string | null>(null);
  const currentLoopRef = useRef(false);

  // RAF for smooth time updates
  const rafIdRef = useRef<number | null>(null);

  // Internal listener handlers
  const handleLoadedMetadata = useCallback(() => {
    const audio = audioRef.current;
    const session = playSessionRef.current;
    if (!audio || session === 0) return;

    const duration = audio.duration;
    if (isFinite(duration) && duration > 0) {
      onDurationChangeRef.current?.(duration);
    }
  }, []);

  const handleDurationChange = useCallback(() => {
    handleLoadedMetadata();
  }, [handleLoadedMetadata]);

  const handlePlay = useCallback(() => {
    const session = playSessionRef.current;
    if (session === 0) return;

    setIsPaused(false);
    isPlayingRef.current?.(true);
  }, []);

  const handlePause = useCallback(() => {
    const session = playSessionRef.current;
    if (session === 0) return;

    // Only fire if we're not in the middle of an internal stop
    // (pause fires during stop, but we don't want to call onPlayingChange in that case)
    if (!isStoppingRef.current) {
      setIsPaused(true);
      isPlayingRef.current?.(false);
    }
  }, []);

  const handleEnded = useCallback(() => {
    const session = playSessionRef.current;
    const id = currentIdRef.current;
    const loop = currentLoopRef.current;
    if (session === 0 || !id) return;

    // Only fire completed if not looping
    if (!loop && isMountedRef.current && currentIdRef.current === id) {
      if (currentTrackingPropsRef.current) {
        captureEvent(EventName.mw_audio_completed, currentTrackingPropsRef.current);
      }
      setPlayingId(null);
      currentIdRef.current = null;
      currentTrackingPropsRef.current = null;
      onEndedRef.current?.();
    }
  }, []);

  const handleError = useCallback(() => {
    const session = playSessionRef.current;
    if (session === 0) return;

    const audio = audioRef.current;
    const error = audio?.error;
    const errorMessage = error ? `${error.code}: ${error.message || "Unknown error"}` : "Unknown audio error";

    if (isMountedRef.current) {
      if (currentTrackingPropsRef.current) {
        captureEvent(EventName.mw_audio_play_error, {
          ...currentTrackingPropsRef.current,
          error_message: errorMessage,
        });
      }
      onErrorRef.current?.(error || errorMessage);
      setPlayingId(null);
      currentIdRef.current = null;
      currentTrackingPropsRef.current = null;
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const session = playSessionRef.current;
    if (session === 0) return;

    const audio = audioRef.current;
    if (!audio || !onTimeUpdateRef.current) return;

    const currentTime = audio.currentTime;
    const duration = audio.duration;

    if (isFinite(currentTime) && isFinite(duration) && duration > 0) {
      onTimeUpdateRef.current(currentTime, duration);
    }
  }, []);

  // RAF loop for smoother time updates (only when onTimeUpdate is provided)
  const startRafLoop = useCallback(() => {
    if (rafIdRef.current) return; // Already running

    const updateTime = () => {
      const session = playSessionRef.current;
      const audio = audioRef.current;
      if (session === 0 || !audio || audio.paused || !onTimeUpdateRef.current) {
        rafIdRef.current = null;
        return;
      }

      const currentTime = audio.currentTime;
      const duration = audio.duration;

      if (isFinite(currentTime) && isFinite(duration) && duration > 0) {
        onTimeUpdateRef.current(currentTime, duration);
      }

      rafIdRef.current = requestAnimationFrame(updateTime);
    };

    rafIdRef.current = requestAnimationFrame(updateTime);
  }, []);

  const stopRafLoop = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, []);

  const stopInternal = useCallback(
    (reason: "switch" | "stop" | "play_error" | "unmount" = "stop") => {
      const audio = audioRef.current;
      if (!audio) return;

      // Set flag to prevent pause handler from firing
      isStoppingRef.current = true;

      // Detach listeners
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("durationchange", handleDurationChange);
      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("timeupdate", handleTimeUpdate);

      // Stop RAF loop
      stopRafLoop();

      // Stop playback and reset
      audio.pause();
      audio.currentTime = 0;
      audio.src = "";
      audio.load();

      // Clear callback refs
      onTimeUpdateRef.current = null;
      onDurationChangeRef.current = null;
      isPlayingRef.current = null;
      onEndedRef.current = null;
      onErrorRef.current = null;

      // Handle pause analytics (only if user-initiated stop, not switch)
      if (reason === "stop" && currentTrackingPropsRef.current) {
        captureEvent(EventName.mw_audio_paused, currentTrackingPropsRef.current);
      }

      // Clear refs
      currentIdRef.current = null;
      currentUrlRef.current = null;
      currentTrackingPropsRef.current = null;
      currentLoopRef.current = false;

      // Reset states
      setIsPaused(false);

      // Reset stopping flag
      isStoppingRef.current = false;
    },
    [
      handleLoadedMetadata,
      handleDurationChange,
      handlePlay,
      handlePause,
      handleEnded,
      handleError,
      handleTimeUpdate,
      stopRafLoop,
    ],
  );

  const stop = useCallback(() => {
    stopInternal("stop");
    setPlayingId(null);
  }, [stopInternal]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio || audio.paused) return;

    // Fire analytics
    if (currentTrackingPropsRef.current) {
      captureEvent(EventName.mw_audio_paused, currentTrackingPropsRef.current);
    }

    // Stop RAF loop
    stopRafLoop();

    // Pause but keep state (don't clear src, don't reset currentTime)
    audio.pause();
    // Note: handlePause will set isPaused = true and call onPlayingChange
  }, [stopRafLoop]);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    // Clamp time to valid range
    const duration = audio.duration;
    if (isFinite(duration) && duration > 0) {
      audio.currentTime = Math.max(0, Math.min(time, duration));
    } else {
      // If duration not loaded yet, still allow seeking (will be clamped when duration loads)
      audio.currentTime = Math.max(0, time);
    }
  }, []);

  const play = useCallback(
    async (opts: NativeAudioPlayOptions) => {
      const {
        id,
        url,
        loop = false,
        startTime,
        trackingProps,
        onTimeUpdate,
        onDurationChange,
        onPlayingChange,
        onEnded,
        onError,
      } = opts;

      // Build full tracking props with audio_id automatically included
      const fullTrackingProps = trackingProps ? { audio_id: id, ...trackingProps } : null;

      // Stop any current playback
      stopInternal("switch");

      // Increment session token for race condition protection
      playSessionRef.current += 1;
      const currentSession = playSessionRef.current;

      // Store callbacks
      onTimeUpdateRef.current = onTimeUpdate || null;
      onDurationChangeRef.current = onDurationChange || null;
      isPlayingRef.current = onPlayingChange || null;
      onEndedRef.current = onEnded || null;
      onErrorRef.current = onError || null;

      // Store tracking props and metadata
      currentTrackingPropsRef.current = fullTrackingProps;
      currentIdRef.current = id;
      currentUrlRef.current = url;
      currentLoopRef.current = loop;

      // Get or create audio element
      const audio = audioRef.current;
      if (!audio) {
        // This should not happen if the component is mounted, but handle it gracefully
        return;
      }

      // Attach listeners
      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("durationchange", handleDurationChange);
      audio.addEventListener("play", handlePlay);
      audio.addEventListener("pause", handlePause);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);
      audio.addEventListener("timeupdate", handleTimeUpdate);

      // Set source and properties
      audio.src = url;
      audio.loop = loop;
      // Set startTime if provided, otherwise start from beginning
      // Note: currentTime will be clamped to valid range when metadata loads
      audio.currentTime = startTime !== undefined ? Math.max(0, startTime) : 0;

      try {
        // Attempt to play
        await audio.play();

        // Check if session is still current (race condition protection)
        if (playSessionRef.current !== currentSession) {
          // Another play() was called, stop this one
          stopInternal("switch");
          return;
        }

        // Start RAF loop if onTimeUpdate is provided
        if (onTimeUpdate) {
          startRafLoop();
        }

        // Fire analytics
        if (fullTrackingProps) {
          captureEvent(EventName.mw_audio_played, fullTrackingProps);
        }

        setPlayingId(id);
      } catch (error) {
        // Check if session is still current
        if (playSessionRef.current !== currentSession) {
          return;
        }

        // Play failed (e.g., autoplay restrictions)
        const errorMessage = error instanceof Error ? error.message : String(error);

        if (fullTrackingProps) {
          captureEvent(EventName.mw_audio_play_error, {
            ...fullTrackingProps,
            error_message: errorMessage,
          });
        }

        onErrorRef.current?.(error);
        stopInternal("play_error");
        setPlayingId(null);
      }
    },
    [
      stopInternal,
      handleLoadedMetadata,
      handleDurationChange,
      handlePlay,
      handlePause,
      handleEnded,
      handleError,
      handleTimeUpdate,
      startRafLoop,
    ],
  );

  const togglePlay = useCallback(
    async (opts: NativeAudioPlayOptions) => {
      const { id, url } = opts;
      const audio = audioRef.current;

      // Same audio that's currently loaded
      if (currentIdRef.current === id && currentUrlRef.current === url && audio && audio.src) {
        if (!audio.paused) {
          // Currently playing → pause
          pause();
          setPlayingId(null);
          return;
        } else if (audio.paused) {
          // Currently paused → resume
          try {
            await audio.play();
            setPlayingId(id);
            // Restart RAF loop if onTimeUpdate callback exists
            if (onTimeUpdateRef.current) {
              startRafLoop();
            }
            // Fire analytics for resume
            if (currentTrackingPropsRef.current) {
              captureEvent(EventName.mw_audio_played, currentTrackingPropsRef.current);
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            if (currentTrackingPropsRef.current) {
              captureEvent(EventName.mw_audio_play_error, {
                ...currentTrackingPropsRef.current,
                error_message: errorMessage,
              });
            }
            opts.onError?.(error);
          }
          return;
        }
      }

      // Different audio or not loaded → play fresh
      await play(opts);
    },
    [pause, play, startRafLoop],
  );

  // Mount/unmount tracking
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopInternal("unmount");
    };
  }, [stopInternal]);

  // Stop on route change
  useEffect(() => {
    stop();
  }, [pathname, stop]);

  return (
    <>
      <audio ref={audioRef} preload='none' style={{ display: "none" }} aria-hidden='true' crossOrigin='anonymous' />
      <NativeAudioContext.Provider value={{ playingId, isPaused, play, togglePlay, pause, stop, seek, audioRef }}>
        {children}
      </NativeAudioContext.Provider>
    </>
  );
}

export function useNativeAudio() {
  const context = useContext(NativeAudioContext);
  if (!context) {
    throw new Error("useNativeAudio must be used within a NativeAudioProvider");
  }
  return context;
}
